import { DocumentTextIcon, StarIcon, UserIcon } from '@heroicons/vue/24/outline';
import { StarIcon as SolidStarIcon } from '@heroicons/vue/24/solid';
import { Calendar, Disc, Library, MicVocal, RadioTower, Speaker } from 'lucide-vue-next';
import { cloneVNode, defineComponent, h, type Component, type VNode } from 'vue';
import BucketDynamicArt from '@/assets/icons/models/model-bucket-dynamic.svg?component';
import BucketMixedArt from '@/assets/icons/models/model-bucket-mixed.svg?component';
import BucketStaticArt from '@/assets/icons/models/model-bucket-static.svg?component';
import GenreArt from '@/assets/icons/models/model-genre.svg?component';
import LocationZoneSubscriber from '@/assets/icons/models/model-location-zone-subscriber.svg?component';
import SubscriberBrandArt from '@/assets/icons/models/model-location-zone-subscriber-brand.svg?component';
// Phosphor "regular" (phosphoricons.com) and Tabler (tabler.io/icons), both MIT.
// Copied as files into src/assets/icons/stock rather than installed: a dozen
// icons do not justify two packages.
import PhosphorBuilding from '@/assets/icons/stock/phosphor-building.svg?component';
import PhosphorBuildings from '@/assets/icons/stock/phosphor-buildings.svg?component';
import PhosphorListNumbers from '@/assets/icons/stock/phosphor-list-numbers.svg?component';
import PhosphorMusicNoteSimple from '@/assets/icons/stock/phosphor-music-note-simple.svg?component';
import PhosphorMusicNotes from '@/assets/icons/stock/phosphor-music-notes.svg?component';
import PhosphorPlaylist from '@/assets/icons/stock/phosphor-playlist.svg?component';
import PhosphorStorefront from '@/assets/icons/stock/phosphor-storefront.svg?component';
import PhosphorUserList from '@/assets/icons/stock/phosphor-user-list.svg?component';
import PhosphorVault from '@/assets/icons/stock/phosphor-vault.svg?component';
import TablerHistory from '@/assets/icons/stock/tabler-history.svg?component';
import { drawPlayerTypeGlyph, PLAYER_TYPES } from '@/composables/playerTypeGlyphs';
import { LOCATION_ZONE_STATUS_PENDING_CANCELATION } from '@/constants/lookupFilterOptions';
import { readBucketId } from '@/utils/nestedRelation';

interface ModelIconEntry {
    icon: Component;
    color: string;
}

/**
 * What puts a record type's colour on an element. The colour travels as
 * `--model-icon-color` and the `model-icon` rule in app.css turns it into
 * `color`, mixed 20% toward white in both light and dark mode. The rule is
 * unlayered, so a `text-*` class at the call site cannot turn a record icon
 * grey.
 */
function tintFor(color: string, extraClass?: string): { class: string; style: Record<string, string> } {
    return { class: extraClass ? `model-icon ${extraClass}` : 'model-icon', style: { '--model-icon-color': color } };
}

/** A compiled render function, as Heroicons export and vite-svg-loader builds. */
type DrawSvg = (ctx: unknown, cache: unknown[]) => VNode;

/**
 * A stock outline icon drawn in a record type's colour, as one stateful
 * component.
 *
 * Heroicons and the SVG files are compiled render functions. The wrapper calls
 * the render itself rather than mounting it as a child component, so each icon
 * on the page is one component instance, not two. Heroicons on their own are
 * functional components, which pass only `class` and `style` to their `<svg>`;
 * as this stateful component's render, a caller's `aria-hidden` and
 * `data-testid` reach the `<svg>` too.
 *
 * Built once per icon, at module load, so callers and tests can compare icons
 * by identity. It must stay stateful: `findComponent` in @vue/test-utils cannot
 * find a functional component, and several tests find icons that way.
 */
function tinted(name: string, draw: DrawSvg, color: string, extraClass?: string): ModelIconEntry {
    const tint = tintFor(color, extraClass);
    const icon = defineComponent({
        name,
        // Vue calls a component's render with its own context and render cache,
        // which the compiled SVG render uses for its static parts.
        render: (ctx: unknown, cache: unknown[]) => cloneVNode(draw(ctx, cache), tint),
    });
    return { icon, color };
}

/** An SVG file's compiled render, from vite-svg-loader's `{ render }` export. */
function svgFile(component: Component): DrawSvg {
    return (component as { render: DrawSvg }).render;
}

/**
 * A Lucide icon in a record type's colour. Lucide icons are functional
 * components that pass every attribute to their `<svg>`, so mounting one as a
 * child adds no component instance. Lucide draws at stroke 2; 1.5 matches
 * Heroicons outline.
 */
function tintedLucide(name: string, base: Component, color: string): ModelIconEntry {
    const tintProps = { ...tintFor(color), strokeWidth: 1.5 };
    const icon = defineComponent({ name, render: () => h(base, tintProps) });
    return { icon, color };
}

const heroicon = (render: unknown): DrawSvg => render as DrawSvg;

const PLAYER_COLOR = '#0a6ba8';

const Account = tinted('AccountIcon', svgFile(PhosphorBuilding), '#0a6ba8');
const Album = tintedLucide('AlbumIcon', Disc, '#008bbf');
const Artist = tintedLucide('ArtistIcon', MicVocal, '#5e488d');
const BucketDynamic = tinted('BucketDynamicIcon', svgFile(BucketDynamicArt), '#5e488d');
// The right bar keeps its own blue; app.css draws it like the rest.
const BucketMixed = tinted('BucketMixedIcon', svgFile(BucketMixedArt), '#67b03e');
const BucketStatic = tinted('BucketStaticIcon', svgFile(BucketStaticArt), '#0a6ba8');
const BucketVault = tinted('BucketVaultIcon', svgFile(PhosphorVault), '#505988');
const ClientDocument = tinted('ClientDocumentIcon', heroicon(DocumentTextIcon), '#562d79');
const Contact = tinted('ContactIcon', heroicon(UserIcon), '#875892');
const ContactCustomPlaylist = tinted('ContactCustomPlaylistIcon', svgFile(PhosphorUserList), '#497c6c');
const CustomPlaylist = tinted('CustomPlaylistIcon', svgFile(PhosphorListNumbers), '#497c6c');
const DdexTrack = tinted('DdexTrackIcon', svgFile(PhosphorMusicNotes), '#a2ae40');
const Franchisee = tinted('FranchiseeIcon', svgFile(PhosphorBuildings), '#497c6c');
const GeneratedPlaylist = tinted('GeneratedPlaylistIcon', svgFile(PhosphorPlaylist), '#57ab46');
const Genre = tinted('GenreIcon', svgFile(GenreArt), '#962890');
const LiveTrack = tinted('LiveTrackIcon', svgFile(PhosphorMusicNoteSimple), '#a2ae40');
const LiveTrackAction = tinted('LiveTrackActionIcon', svgFile(TablerHistory), '#ef9f27');
const Location = tinted('LocationIcon', svgFile(PhosphorStorefront), '#497c6c');
const LocationZone = tinted('LocationZoneIcon', heroicon(StarIcon), '#f68921');
const LocationZoneCustomPlaylistSchedule = tintedLucide('PlaylistScheduleIcon', Calendar, '#497c6c');
const LocationZoneMaster = tinted('LocationZoneMasterIcon', heroicon(StarIcon), '#e8802e');
const LocationZonePlayer = tintedLucide('LocationZonePlayerIcon', Speaker, PLAYER_COLOR);
// The zone's orange, by Joel's choice (2026-10-27).
const RecordLabel = tintedLucide('RecordLabelIcon', Library, '#f68921');
const Station = tintedLucide('StationIcon', RadioTower, '#008bbf');

/**
 * A player drawn as its player type (physical, virtual, mobile or web), in the
 * player colour, keyed by the player_types row id. A player whose type is not
 * known keeps the speaker.
 */
const PLAYER_TYPE_ICONS: Readonly<Record<number, ModelIconEntry>> = Object.freeze(
    Object.fromEntries(
        Object.entries(PLAYER_TYPES).map(([id, type]) => [
            id,
            tinted(`LocationZonePlayer${type.key[0].toUpperCase()}${type.key.slice(1)}Icon`, () => drawPlayerTypeGlyph(type.key), PLAYER_COLOR),
        ])
    )
);

/** A player type's icon, or null for an unknown type. */
export function playerTypeIcon(playerTypeId: number | null | undefined): Component | null {
    return typeof playerTypeId === 'number' ? (PLAYER_TYPE_ICONS[playerTypeId]?.icon ?? null) : null;
}

/** The player type id a search row carries as `player_type: {id, display_string}`, or null. */
function playerTypeIdOf(row: Record<string, unknown>): number | null {
    const playerType = row.player_type;
    if (playerType && typeof playerType === 'object' && 'id' in playerType) {
        const id = (playerType as { id?: unknown }).id;
        return typeof id === 'number' ? id : null;
    }
    return null;
}

type ZoneBrand = 'grayv' | 'tpg';

/**
 * A zone's mark in its brand's colour, when the row says which brand it is:
 * a filled star for a plain or master zone, and two rings with a filled centre
 * for a subscriber. These carry `model-icon-brand`, which app.css draws in the
 * exact brand colour in dark mode, and a deeper shade of it in light mode,
 * instead of the 20% mix toward white.
 * Zones without the fact, such as the sidebar's Zones link or a
 * nested reference the lookup has not answered yet, keep the orange outline
 * star or the subscriber's own art.
 */
function branded(name: string, draw: DrawSvg, brand: ZoneBrand): ModelIconEntry {
    return tinted(name, draw, `var(--brand-${brand})`, 'model-icon-brand');
}

const BRANDED_ZONE_MARKS: Readonly<Record<ZoneBrand, { plain: ModelIconEntry; master: ModelIconEntry; subscriber: ModelIconEntry }>> =
    Object.freeze({
        grayv: {
            plain: branded('LocationZoneGrayVIcon', heroicon(SolidStarIcon), 'grayv'),
            master: branded('LocationZoneMasterGrayVIcon', heroicon(SolidStarIcon), 'grayv'),
            subscriber: branded('LocationZoneSubscriberGrayVIcon', svgFile(SubscriberBrandArt), 'grayv'),
        },
        tpg: {
            plain: branded('LocationZoneTpgIcon', heroicon(SolidStarIcon), 'tpg'),
            master: branded('LocationZoneMasterTpgIcon', heroicon(SolidStarIcon), 'tpg'),
            subscriber: branded('LocationZoneSubscriberTpgIcon', svgFile(SubscriberBrandArt), 'tpg'),
        },
    });

/**
 * A zone is TPG when `is_tpg_location_zone` is set and Gray V when it is not.
 * A row that does not carry the field at all says neither, so it gets no brand
 * rather than defaulting to Gray V: a partial reference to a TPG zone must not
 * show the wrong brand.
 */
function zoneBrand(row: Record<string, unknown>): ZoneBrand | null {
    const isTpg = row.is_tpg_location_zone;
    if (isTpg === true || isTpg === 1) return 'tpg';
    if (isTpg === false || isTpg === 0) return 'grayv';
    return null;
}

/**
 * Puts a `<title>` first inside the SVG, which the browser shows as the icon's
 * tooltip. An SVG ignores a `title` attribute, so it has to be the element.
 */
function withTitle(draw: DrawSvg, title: string): DrawSvg {
    return (ctx: unknown, cache: unknown[]) => {
        const svg = draw(ctx, cache);
        const children = Array.isArray(svg.children) ? svg.children : [];
        return h(svg.type as string, { ...svg.props }, [h('title', title), ...children]);
    };
}

/**
 * A zone whose status is Pending Cancelation draws its mark in the theme's red,
 * whatever its brand, so a zone that is about to leave stands out wherever it
 * appears. Its tooltip says "Pending Cancelation", so the red is not the only
 * sign. The shape still says plain, master or subscriber. These carry
 * `model-icon-pending-cancelation`, which app.css draws in the exact red
 * instead of the 20% mix toward white.
 */
function pendingCancelation(name: string, draw: DrawSvg): ModelIconEntry {
    return tinted(
        name,
        withTitle(draw, LOCATION_ZONE_STATUS_PENDING_CANCELATION.display_string),
        'var(--destructive-foreground)',
        'model-icon-pending-cancelation',
    );
}

/** Exported so tests can compare icons by identity. */
export const PENDING_CANCELATION_ZONE_MARKS = Object.freeze({
    plain: pendingCancelation('LocationZonePendingCancelationIcon', heroicon(SolidStarIcon)),
    master: pendingCancelation('LocationZoneMasterPendingCancelationIcon', heroicon(SolidStarIcon)),
    subscriber: pendingCancelation('LocationZoneSubscriberPendingCancelationIcon', svgFile(SubscriberBrandArt)),
});

function isPendingCancelation(row: Record<string, unknown>): boolean {
    return Number(row.location_zone_status_id) === LOCATION_ZONE_STATUS_PENDING_CANCELATION.id;
}

/**
 * The icon for a model, so a record looks the same wherever it appears — the
 * section sidebar, an inspector header, a relation link.
 *
 * Each icon carries its record type's colour, so callers only size it. Zones
 * are the exception: a zone row that says its brand draws a branded mark (see
 * {@link BRANDED_ZONE_MARKS}), and a subscriber without one keeps its own
 * two-tone art.
 *
 * Not every entity has an icon and that is fine: callers render nothing when
 * the lookup misses, so a model without artwork simply shows its label. That
 * is deliberately quieter than a placeholder glyph, which would read as a real
 * icon and be wrong.
 *
 * A few entities have variants that depend on the RECORD, not the model —
 * see {@link modelIconForRow}.
 */
const MODEL_ICONS: Readonly<Record<string, ModelIconEntry>> = Object.freeze({
    Account,
    Album,
    Artist,
    // The model-level mark, for "buckets" as a CATEGORY (the sidebar link).
    // A specific bucket resolves its own variant through modelIconForRow, which
    // returns null rather than falling back here — this artwork is the static
    // variant, so a fallback would quietly label the record static.
    Bucket: BucketStatic,
    Contact,
    ContactLoginCustomPlaylist: ContactCustomPlaylist,
    ContactLoginStation: Station,
    Genre,
    LiveTrack,
    LiveTrackAction,
    Location,
    LocationZone,
    LocationZoneCustomPlaylist: CustomPlaylist,
    LocationZoneCustomPlaylistSchedule,
    // The model-level mark. A specific player draws its TYPE instead (physical,
    // virtual, mobile, web) through modelIconForRow.
    LocationZonePlayer,
    RawDdexTrack: DdexTrack,
    RecordLabel,
    // Not searchable entities, but they appear as relations and in the legacy
    // admin, so they get the same treatment.
    ClientDocument,
    Franchisee,
    GeneratedPlaylist,
});

/** The icon for an entity, or null when that model has none. */
export function modelIcon(entityId: string | null | undefined): Component | null {
    if (!entityId) {
        return null;
    }
    return MODEL_ICONS[entityId]?.icon ?? null;
}

/** The record type's colour, for surfaces that tint around its icon, or null when that model has none. */
export function modelIconTint(entityId: string | null | undefined): ReturnType<typeof tintFor> | null {
    const color = entityId ? MODEL_ICONS[entityId]?.color : undefined;
    return color ? tintFor(color) : null;
}

/**
 * The four `bucket_type` slugs a search document can carry, which are the
 * bucket_types rows' names lowercased (BucketType::SLUGS in tabasco).
 *
 * The filter picker does NOT share this list -- it filters bucket_type_id through
 * the bundled id lookup in `@/constants/lookupFilterOptions`. The two are tied
 * together by a test that resolves a mark for every bundled option's label, so a
 * type added to one and missed in the other fails there.
 *
 * Private and frozen, like {@link MODEL_ICONS}. Callers that need a bucket's mark
 * go through {@link modelIconForRow}, so they pick up the fallback rules in
 * {@link bucketIconForRow} too rather than only the slug lookup.
 */
const BUCKET_TYPES = Object.freeze({
    1: { slug: 'static', icon: BucketStatic.icon },
    2: { slug: 'dynamic', icon: BucketDynamic.icon },
    3: { slug: 'mixed', icon: BucketMixed.icon },
    4: { slug: 'vault', icon: BucketVault.icon },
} as const);

type BucketTypeSlug = (typeof BUCKET_TYPES)[keyof typeof BUCKET_TYPES]['slug'];

const BUCKET_TYPE_ICONS: Readonly<Record<BucketTypeSlug, Component>> = Object.freeze(
    Object.values(BUCKET_TYPES).reduce(
        (icons, type) => Object.assign(icons, { [type.slug]: type.icon }),
        {} as Record<BucketTypeSlug, Component>,
    ),
);

const BUCKET_TYPE_SLUGS = Object.values(BUCKET_TYPES).map((type) => type.slug) as readonly BucketTypeSlug[];

/**
 * The mark for a bucket_types row id.
 *
 * Callers holding the id should use this rather than lowercasing a display name:
 * the id is stable and the name is not. The lookup file's own header says a row
 * may be renamed in production, and a rename would silently blank a name-keyed
 * lookup with no error to notice.
 */
export function bucketIconForTypeId(id: number | string | null | undefined): Component | null {
    if (id === null || id === undefined || id === '') return null;
    return BUCKET_TYPES[Number(id) as keyof typeof BUCKET_TYPES]?.icon ?? null;
}

/**
 * Derived from the slug list, not from the icon map: the slugs are what the backend
 * defines, and reading membership off a map would let an edit to that map silently
 * widen or narrow a type predicate.
 */
function isBucketTypeSlug(value: unknown): value is BucketTypeSlug {
    return typeof value === 'string' && (BUCKET_TYPE_SLUGS as readonly string[]).includes(value);
}

function indexedBucketIcon(row: Record<string, unknown> | null | undefined): Component | null {
    const type = row?.bucket_type;
    return isBucketTypeSlug(type) ? BUCKET_TYPE_ICONS[type] : null;
}

/**
 * Vaults use the vault mark (`is_base_bucket`) and are not expected to have
 * genres. Buckets that follow a vault default to static. Otherwise, resolve
 * static, dynamic, or mixed from the bucket's own genre assignments.
 * Partial records without genre data stay blank until they are loaded.
 */
function bucketIconForRow(row: Record<string, unknown>): Component | null {
    const indexedIcon = indexedBucketIcon(row);
    if (indexedIcon) return indexedIcon;

    if (row.is_base_bucket === true) return BucketVault.icon;

    // readBucketId, because a bucket with no vault stores base_bucket_id = 0.
    if (readBucketId(row.base_bucket_id) !== null && row.ignores_base_bucket_genres !== true) {
        return BucketStatic.icon;
    }

    if (!Array.isArray(row.bucket_genres)) return null;

    const genres = row.bucket_genres as Array<{ is_dynamic?: boolean }>;
    const dynamicCount = genres.filter((genre) => genre?.is_dynamic === true).length;
    if (dynamicCount === 0) return BucketStatic.icon;
    return dynamicCount === genres.length ? BucketDynamic.icon : BucketMixed.icon;
}

/**
 * Three models are drawn differently depending on the record in hand.
 *
 * A player is drawn as its player type, read from `player_type.id`. A player
 * whose type is not known keeps the model's speaker.
 *
 * A zone is a plain zone, a master, or a subscriber — three different things
 * that a single zone icon cannot say apart. A subscriber names its master in
 * `master_location_zone_id`; a master is known by having subscribers, which
 * the search document carries as `subscribers_count` (the backend keeps that
 * count on the master's own document and reindexes it on subscribe).
 * A zone whose `location_zone_status_id` is Pending Cancelation keeps its
 * shape but is drawn red (see {@link PENDING_CANCELATION_ZONE_MARKS}).
 *
 * For buckets see {@link bucketIconForRow}, which returns null when the record
 * does not settle the question. Callers already render nothing for a null, so
 * an undecided bucket shows its name with no mark instead of a wrong one.
 *
 * Every field read here has to exist in the generated `models.json`
 * `_searchable` list — `modelIcons.test.ts` pins that. Both variants first
 * shipped reading invented names (`is_vault`, `bucket_variant`,
 * `is_master_location_zone`), which made those branches unreachable in the app
 * while their tests passed on the same invented names.
 */
export function modelIconForRow(
    entityId: string | null | undefined,
    row: Record<string, unknown> | null | undefined
): Component | null {
    if (entityId === 'LocationZone' && row) {
        const brand = zoneBrand(row);
        const marks = isPendingCancelation(row) ? PENDING_CANCELATION_ZONE_MARKS : brand ? BRANDED_ZONE_MARKS[brand] : null;
        const subscribersCount = row.subscribers_count;
        if (typeof subscribersCount === 'number' && subscribersCount > 0) {
            return (marks ? marks.master : LocationZoneMaster).icon;
        }
        // Production stores "no master" as NULL or as 0, so only a real id counts.
        const masterId = Number(row.master_location_zone_id);
        if (row.master_location_zone_id != null && Number.isInteger(masterId) && masterId > 0) {
            return marks ? marks.subscriber.icon : LocationZoneSubscriber;
        }
        if (marks) return marks.plain.icon;
    }
    if (entityId === 'LocationZonePlayer' && row) {
        const typeIcon = playerTypeIcon(playerTypeIdOf(row));
        if (typeIcon) return typeIcon;
    }
    if (entityId === 'Bucket') {
        // No row at all is also "cannot decide" — and for a bucket the generic
        // fallback below would mean the static mark, not a neutral one.
        return row ? bucketIconForRow(row) : null;
    }
    return modelIcon(entityId);
}

/** Entity ids that have an icon. Exported for the coverage test. */
export const ENTITIES_WITH_ICONS = Object.freeze(Object.keys(MODEL_ICONS));

/**
 * Whether this row already answers "which bucket mark?", so no fetch is needed.
 *
 * A full bucket search document always carries `bucket_genres` — the indexer
 * serializes the collection even when it is empty — so that array is what says
 * "this is the whole record". `is_base_bucket` covers the shortcut
 * where a custom list settles a vault source bucket for free.
 *
 * Nested references carry `bucket_type`, so they also settle the icon without
 * fetching the full bucket. Older documents retain the genre-based fallback.
 */
export function hasBucketIconData(row: Record<string, unknown> | null | undefined): boolean {
    return indexedBucketIcon(row) !== null || Array.isArray(row?.bucket_genres) || row?.is_base_bucket === true;
}
